import * as React from 'react';
import { ChevronDown, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger, getAddRepoDrawerPortal, usePopoverListWheelScroll } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface ComboboxOption {
  value: string;
  label: string;
}

export interface ComboboxProps {
  data: ComboboxOption[];
  value: string | string[] | null;
  onSelect: (val: string | number | (string | number)[]) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  multiple?: boolean;
  valueKey?: string;
  labelKey?: string;
  showMultipleTags?: boolean;
  id?: string;
  showClear?: boolean;
  onClear?: () => void;
  contentClassName?: string;
}

function Combobox({
  data,
  value,
  onSelect,
  placeholder = 'Select…',
  disabled = false,
  error = false,
  multiple = false,
  id,
  showClear = false,
  onClear,
  contentClassName,
}: ComboboxProps) {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const listRef = usePopoverListWheelScroll(open);
  const portalContainer = getAddRepoDrawerPortal();

  const selectedValues = React.useMemo(
    () => (multiple ? (Array.isArray(value) ? value.map(String) : []) : [String(value ?? '')]),
    [multiple, value],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return data;
    return data.filter(
      item => item.label.toLowerCase().includes(q) || item.value.toLowerCase().includes(q),
    );
  }, [data, query]);

  const selectedLabels = React.useMemo(() => {
    if (!multiple) {
      const match = data.find(item => item.value === String(value ?? ''));
      return match?.label ?? '';
    }
    return data
      .filter(item => selectedValues.includes(item.value))
      .map(item => item.label);
  }, [data, multiple, selectedValues, value]);

  const toggleValue = (optionValue: string) => {
    if (!multiple) {
      onSelect(optionValue);
      setOpen(false);
      setQuery('');
      return;
    }

    const next = new Set(selectedValues.filter(Boolean));
    if (next.has(optionValue)) next.delete(optionValue);
    else next.add(optionValue);
    onSelect(Array.from(next));
  };

  const removeTag = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!multiple) return;
    onSelect(selectedValues.filter(v => v !== optionValue));
  };

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClear?.();
  };

  const hasSelection = multiple
    ? selectedValues.filter(Boolean).length > 0
    : Boolean(value);

  return (
    <Popover modal={Boolean(portalContainer)} open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="ghost"
          disabled={disabled}
          className={cn(
            'box-border flex h-10 w-full min-w-0 max-w-full items-center justify-between gap-2 rounded-[var(--inv-radius-control,12px)] border border-solid bg-[var(--inv-surface)] px-3 py-0 text-left text-sm font-normal text-[var(--inv-text)] shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))] transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)]',
            error ? 'border-[#fecdca]' : 'border-[var(--inv-border)]',
            disabled && 'cursor-not-allowed opacity-50',
            multiple && 'min-h-10 h-auto py-2',
          )}>
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 font-normal">
            {multiple && Array.isArray(selectedLabels) && selectedLabels.length > 0 ? (
              selectedValues
                .filter(Boolean)
                .map(val => {
                  const label = data.find(item => item.value === val)?.label ?? val;
                  return (
                    <span
                      key={val}
                      className="inline-flex max-w-full items-center gap-1 rounded-full bg-[var(--inv-accent-soft)] px-2 py-0.5 text-xs font-medium text-[var(--inv-accent)]">
                      <span className="truncate">{label}</span>
                      <Button
                        type="button"
                        variant="ghost"
                        className="border-0 bg-transparent p-0 text-[var(--inv-accent)]"
                        onClick={e => removeTag(val, e)}
                        aria-label={`Remove ${label}`}>
                        <X className="h-3 w-3" />
                      </Button>
                    </span>
                  );
                })
            ) : !multiple && selectedLabels ? (
              <span className="truncate font-normal">{selectedLabels}</span>
            ) : (
              <span className="truncate font-normal text-[var(--inv-text-muted)]">{placeholder}</span>
            )}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {showClear && hasSelection && onClear ? (
              <Button
                type="button"
                variant="ghost"
                className="border-0 bg-transparent p-0 text-[var(--inv-text-muted)] hover:text-[var(--inv-text)]"
                onClick={handleClear}
                aria-label="Clear selection">
                <X className="h-4 w-4" />
              </Button>
            ) : null}
            <ChevronDown className="h-4 w-4 text-[var(--inv-text-muted)]" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        avoidCollisions={false}
        container={portalContainer}
        onOpenAutoFocus={event => event.preventDefault()}
        onCloseAutoFocus={event => event.preventDefault()}
        className={cn(
          'w-[var(--radix-popover-trigger-width)] overflow-hidden rounded-[10px] border border-solid border-[var(--inv-border)] bg-white p-0 shadow-[0_8px_24px_rgba(43,33,24,0.12)]',
          contentClassName,
        )}>
        <div className="bg-white p-2">
          <input
            type="search"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Search..."
            className="box-border h-9 w-full appearance-none rounded-lg [border-width:1px] [border-color:var(--inv-border-soft,#e8e0d6)] bg-white px-3 text-sm font-normal text-[var(--inv-text)] placeholder:font-normal placeholder:text-[var(--inv-text-muted)] shadow-none outline-none focus-visible:outline-none focus-visible:ring-0"
          />
        </div>
        <ul
          ref={listRef}
          className="m-0 max-h-56 list-none overflow-y-auto overscroll-contain bg-white p-0 py-1 [scrollbar-color:var(--inv-border-strong,#cfc4b6)_transparent] [scrollbar-width:thin]"
          role="listbox">
          {filtered.length === 0 ? (
            <li className="list-none px-3 py-2.5 text-sm font-normal text-[var(--inv-text-muted)]">
              No results
            </li>
          ) : (
            filtered.map(item => {
              const isSelected = multiple
                ? selectedValues.includes(item.value)
                : String(value ?? '') === item.value;
              return (
                <li key={item.value} className="list-none">
                  <Button
                    type="button"
                    variant="ghost"
                    role="option"
                    aria-selected={isSelected}
                    className={cn(
                      'h-auto w-full justify-start rounded-none border-0 px-3 py-2.5 text-left text-sm font-normal shadow-none transition-colors hover:translate-y-0',
                      isSelected
                        ? 'bg-[var(--inv-accent)] text-[var(--inv-on-accent)] hover:bg-[var(--inv-accent)] hover:text-[var(--inv-on-accent)]'
                        : 'bg-transparent text-[var(--inv-text)] hover:bg-[var(--inv-accent)] hover:text-[var(--inv-on-accent)]',
                    )}
                    onClick={() => toggleValue(item.value)}>
                    <span className="truncate font-normal">{item.label}</span>
                  </Button>
                </li>
              );
            })
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

export { Combobox };
